import { ApiCallResponse } from "../app/api";
import { BaseApp } from "../app";
import { Attribute, AttributeKey } from "../db/models/attributes";
import { SyncMessage } from "../db/models/synchronizer";
import SyncCommand, { SyncCommandLike, SyncCommandData } from "../db/models/syncCommand";
import { DexieTable } from "../db/index";
import { EventSource } from "../event-source/index";


export default class Synchronizer extends EventSource
{
    private app: BaseApp;
    private syncTables: Dexie.Table<any, any>[];
    private currentlySynchronizing: boolean;
    private isNextPullScheduled = false;
    private lastAutoSyncResult: AutoSyncResult;
    private _lastSyncTimestamp: Attribute;

    public get lastSyncTimestamp() {return this._lastSyncTimestamp; }

    constructor(app: BaseApp)
    {
        super();
        this.app = app;
        this.currentlySynchronizing = false;
        this.syncTables = [];

        for(let modelName of this.app.settings.clientSync.models)
        {
            this.syncTables.push(this.app.db[modelName]);
        }
    }

    private getLastSync(): PromiseLike<Attribute>
    {
        return this.app.db.Attribute.get(AttributeKey.LAST_SYNC);
    }

    private setLastSync(timestamp: Date): PromiseLike<any>
    {
        return this.app.db.Attribute.put({key: AttributeKey.LAST_SYNC, value: timestamp});
    }

    public pullUpdates(queueNext = false): PromiseLike<void>
    {
        return new Promise((resolve, reject) =>
        {
            if (this.currentlySynchronizing)
            {
                reject(new Error('Synchronization process is already running! Please try again later.'));
            }
            if (!navigator.onLine)
            {
                resolve();
                console.warn('Warning: Offline, unable to sync.');
            }
            this.currentlySynchronizing = true;

            this.getLastSync().then((lastSync) =>
            {
                this._lastSyncTimestamp = lastSync;

                let args: {[key: string]: Object} = {version: this.app.settings.version.number};
                if (lastSync != null)
                {   
                    args.modified_at = lastSync.value;
                }
                
                return this.app.api.private(this.app.settings.clientSync.apiEndpoints.pull, args);
            }).then((response: ApiCallResponse) =>
            {   
                let syncTime = new Date(response.timestamp);    

                this.app.db.transaction('rw', this.syncTables, this.processPullResponse(response)).then(() =>
                {
                    return this.setLastSync(syncTime); 
                }).then(() =>
                {
                    this.currentlySynchronizing = false;
                    this.afterPull(queueNext);
                    return this.processCommands();
                }).then(resolve);
            }).then(() =>
            {

            }, (error) =>
            {
                this.currentlySynchronizing = false;
                this.afterPull(queueNext);
                reject(error);
            });
        });
    }

    private afterPull(queueNext: boolean)
    {
        if (queueNext)
        {
            console.info(`[ClientSync]: Next sync in ${this.app.settings.clientSync.autoSyncInterval/1000} seconds.`);
            this.trigger('autoSyncScheduled');
            setTimeout(() =>
            {
                this.trigger('autoSyncBegin');
                this.pullUpdates(queueNext).then(() =>
                {
                    this.trigger('autoSyncEnd', {result: this.lastAutoSyncResult});
                });
            
            }, this.app.settings.clientSync.autoSyncInterval);
        }
    }

    public push(message: SyncMessage, update = true): PromiseLike<void>
    {
        if (message.timestamp === undefined)
        {
            message.timestamp = new Date();
        }

        return this.app.db.SyncMessage.add(message).then((id) =>
        {
            return this.pushMessages();
        }).then(() =>
        {
            if (update)
            {
                return this.pullUpdates().then(() =>
                {
                    return this.app.refreshCurrentView();
                })
            }
            else
            {
                return Promise.resolve();
            }
        });
    }
    
    public pushMessages(): PromiseLike<void>
    {
        if (!navigator.onLine) 
        {
            return Promise.resolve();
        }

        let processedMessages: SyncMessage[];
        return this.app.db.SyncMessage.orderBy('timestamp').toArray().then(messages => 
        {
            processedMessages = messages;

            if (messages.length > 0)
            {
                return this.app.api.private(this.app.settings.clientSync.apiEndpoints.push, {messages: messages});
            }
            else
            {
                return Promise.resolve();
            }
        }).then((response) =>
        {
            let keys = processedMessages.map(item => item.id);
            return this.app.db.SyncMessage.bulkDelete(keys);
        });
    }
    
    /**
     * Update synchronizer: Push messages and then pull updates.
     */
    public update(queueNext = false)
    {
        return this.pushMessages().then(() =>
        {
            return this.pullUpdates(queueNext);
        });
    }

    public createMessageAndPull(message: SyncMessage, refreshView = true): PromiseLike<void>
    {
        return this.push(message, false).then(() =>
        {
            return this.pullUpdates();
        }).then(() =>
        {
            if (refreshView)
            {
                return this.app.refreshCurrentView();
            }
        });
    }
    
    private processCommands(): PromiseLike<void>
    {
        return this.app.db.SyncCommand.toArray().then((commands) =>
        {
            let promises: PromiseLike<void>[] = [];
    
            for(let cmd of commands)
            {
                switch(cmd.action)
                {
                    case SyncCommand.ACTION_REMOVE_INSTANCE:
                        let data: SyncCommandData = JSON.parse(cmd.data);
                        let table = this.app.db[data.name] as DexieTable;
                        if (table != null)
                        {
                            promises.push(table.delete(data.key));
                        }
                        break;
                    case SyncCommand.ACTION_UPDATE_CONSTANCE:
                        promises.push(this.requestConstance());
                        break;
                    case SyncCommand.ACTION_FULL_RESET:
                        if (this._lastSyncTimestamp != null)
                        {
                            indexedDB.deleteDatabase(this.app.db.name);
                            location.reload();
                        }
  
                        break;
                    case SyncCommand.ACTION_APP_UPDATE:
                        if (this._lastSyncTimestamp != null)
                        {
                            this.app.promptDownloadUpdate();
                        }
                        break;
                    default:
                        console.error(`Unsupported sync command action "${cmd.action}", data:`, cmd.data);
                }
            }
            
            return Promise.all(promises).then(() =>
            {
                return this.app.db.SyncCommand.bulkDelete(commands.map(o => o.id));
            });
        });
    }

    private processPullResponse(response: ApiCallResponse)
    {
        return () =>
        {
            let stats = {count: 0, objects: []};

            try
            {
                for (let table of this.syncTables)
                {
                    let objects = response[table.name];
                    
                    if (Array.isArray(objects))
                    {
                        for (let obj of objects)
                        {
                            table.put(obj);
                            stats.objects.push(obj);
                            stats.count++;
                        }
                    }
                }

                console.info(`[ClientSync]: Updated ${stats.count} objects.`, stats.objects);
                
                if (stats.count === 0)
                    this.lastAutoSyncResult = AutoSyncResult.NO_CHANGES;
                else
                    this.lastAutoSyncResult = AutoSyncResult.CHANGED;
            }
            catch (error)
            {
                throw error;
            }
        }
    }

    public requestConstance(): PromiseLike<void>
    {
        return this.app.api.private(this.app.settings.clientSync.apiEndpoints.constance, {}).then(constance =>
        {
            this.app.constance = constance;

            return this.app.db.Attribute.put({
                key: AttributeKey.CONSTANCE,
                value: constance
            });
        }).then((key) =>
        {
            //flatten
        });
    }
}

export enum AutoSyncResult
{
    NO_CHANGES = 'no_changes',
    CHANGED = 'modified'
}

export interface AutoSyncEndEvent
{
    result: AutoSyncResult;
}